<template>
  <span v-if="items.length" class="location-social-handles">
    <template v-for="(identity, index) in items" :key="identity.platform">
      <span v-if="index > 0" class="location-social-handles__sep"> · </span>
      <a v-if="identity.href" class="location-social-handles__item" :href="identity.href" target="_blank" rel="noopener noreferrer" @click.stop>
        <IconEmbedded :name="identity.platform" :size="14" color="#667085" />
        <span>{{ identity.label }}</span>
      </a>
      <span v-else class="location-social-handles__item">
        <IconEmbedded :name="identity.platform" :size="14" color="#667085" />
        <span>{{ identity.label }}</span>
      </span>
      <!-- Outside the link so hovering the warning doesn't navigate to the profile. -->
      <TooltipWrapper v-if="identity.isPrivate" class="location-social-handles__private" :text="PRIVATE_INSTAGRAM_TAG_WARNING" :width="260">
        <template #hoverableElement>
          <span class="location-social-handles__private-icon" aria-label="Private Instagram account">
            <IconEmbedded name="warning_2-5" :size="14" color="#b54708" />
          </span>
        </template>
      </TooltipWrapper>
    </template>
  </span>
</template>

<script setup lang="ts">
import { computed } from "vue";
import IconEmbedded from "@components/ui/IconEmbedded.vue";
import TooltipWrapper from "@components/ui/TooltipWrapper.vue";
import { PRIVATE_INSTAGRAM_TAG_WARNING } from "@/constants/social";
import type { IconName } from "@contracts/IconName";
import type { LocationData } from "@contracts/socialLocations";
import { buildFacebookPageUrl, buildInstagramProfileUrl, buildTikTokProfileUrl } from "@logic/SocialFieldInputCleaner";

interface IdentityItem {
  platform: IconName;
  label: string;
  href: string;
  /** Instagram only: the account is private, so photo tags are skipped at publish. */
  isPrivate?: boolean;
}

const props = defineProps<{
  location?: LocationData | null;
}>();

const items = computed((): IdentityItem[] => {
  const socialSet = props.location?.socialSet;
  if (!socialSet) return [];
  const result: IdentityItem[] = [];
  if (socialSet.instagramHandle) {
    result.push({
      platform: "instagram",
      label: `${socialSet.instagramHandle}`,
      href: buildInstagramProfileUrl(socialSet.instagramHandle),
      isPrivate: socialSet.instagramIsPrivate === true,
    });
  }
  if (socialSet.facebookPageName) {
    result.push({
      platform: "facebook",
      label: socialSet.facebookPageName,
      href: buildFacebookPageUrl(socialSet.facebookPageUrl, socialSet.facebookPageId),
    });
  }
  if (socialSet.tikTokHandle) {
    result.push({
      platform: "tiktok",
      label: `${socialSet.tikTokHandle}`,
      href: buildTikTokProfileUrl(socialSet.tikTokHandle),
    });
  }
  return result;
});
</script>

<style scoped lang="scss">
.location-social-handles {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 2px 0;
  color: #667085;
  font-size: 90%;
}
.location-social-handles__item {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  color: inherit;
  text-decoration: none;
}
a.location-social-handles__item:hover {
  text-decoration: underline;
}
.location-social-handles__private {
  margin-left: 4px;
}
.location-social-handles__private-icon {
  display: inline-flex;
  cursor: help;
}
.location-social-handles__sep {
  margin: 0 4px;
}
</style>
